import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';

import { useApp } from 'contexts/AppContext';
import { useNotif } from 'contexts/NotifContext';
import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import { simplifiedRights } from 'utils/constants';
import { filterAllRows } from 'utils/functions';
import moment from 'utils/moment';
import pluralize from 'utils/pluralize';
import {
  ACTIVATION,
  ADMINRIGHTS,
  CREATE_SHOP,
  DATA_PROVIDERS,
  DOMAINCONFIG,
  GROUP,
  USERPROFILE,
  USERS,
  VIEWS
} from 'utils/rest';

import { ButtonCreate } from 'components/ButtonCreate';
import { Text, Tooltip } from 'components/DataDisplay';
import { MultipleSelectSearch, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';
import ModalLogs from 'components/ModalLogs';
import { TableData } from 'components/TableData';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { ModalDelete } from './components/ModalDelete';
import { ModalForm } from './components/ModalForm';
import { ModalStepper } from './components/ModalStepper';

const UserContainer = () => {
  const { themeColors } = useTheme();
  const { domainConfigs, isSimplify, domainConfig } = useApp();
  const {
    adminRights: adminRightsContext,
    setAdminRights,
    user,
    fetchUser
  } = useUser();

  let timer;

  const [adminPresetList, setAdminPresetList] = useState([]);
  const [domainList, setDomainList] = useState([]);
  const [roles, setRoles] = useState({});
  const [rows, setRows] = useState([]);
  const [userList, setUserList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openModal, setModal] = useState(null);
  const [selectedUser, setSelectedUser] = useState({});
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalUser, setTotalUser] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [shopGroups, setShopGroups] = useState([]);
  const [restrictedProfiles, setRestrictedProfiles] = useState([]);
  const [restrictedGroups, setRestrictedGroups] = useState([]);
  const [profileOptions, setProfileOptions] = useState([]);
  const [networkGroups, setNetworkGroups] = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [viewList, setViewList] = useState([]);
  const [dataProviders, setDataProviders] = useState([]);
  const { notify } = useNotif();
  const [logs, setLogs] = useState({
    sessionStart: []
  });

  const closeModal = () => {
    setModal(null);
    setLogs({ sessionStart: [] });
    setSelectedUser({});
  };

  const selectUser = async id => {
    const foundUser = userList.find(el => el._id === id);
    const [currentUser, adminRights] = await Promise.all([
      USERS.getByMongoId(id),
      ADMINRIGHTS.getRightsById(id)
    ]);

    const finalUser = {
      ...foundUser,
      ...currentUser,
      adminRights: adminRights[0]
    };

    setSelectedUser(finalUser);
    return finalUser;
  };

  const getUsers = async () => {
    try {
      // si aucun profils pas besoin d'appeler la route.. on
      if (restrictedProfiles.length === 0) {
        setTotalUser(0);
        return [];
      }

      const { result, count } = await USERS.getPaginated({
        limit,
        page: pageNumber + 1,
        search: searchFieldValue,
        profiles: restrictedProfiles.map(el =>
          el.value === 'none' ? '' : el.value
        ),
        groups: restrictedGroups.map(el => el.value)
      });
      setTotalUser(count);
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getDataProviders = async () => {
    try {
      const data = await DATA_PROVIDERS.get();
      return data;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getRoles = async () => {
    try {
      const result = await USERS.getRoles();
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getAdminRightsPresets = async () => {
    try {
      const result = await ADMINRIGHTS.getPresetList();
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const fetchGroups = async query => {
    const { data = [], totalCount } = await GROUP.getGroupSearch({
      search: query
    });
    return {
      data: data.map(el => ({ label: el.name, value: el._id })),
      totalCount
    };
  };

  const getProfiles = async () => {
    try {
      const allDomainProfiles = await DOMAINCONFIG.getDomainProfiles(
        user.domain
      );

      const profiles = await USERPROFILE.get();
      let domainProfiles = [
        { key: 'none', name: 'Aucun profil' },
        ...allDomainProfiles
      ];

      if (user.profile) {
        const userProfile = profiles.find(el => el.key === user.profile);
        if (userProfile?.features?.profilesAccess?.length) {
          domainProfiles = allDomainProfiles.filter(profile =>
            userProfile.features.profilesAccess.includes(profile.key)
          );
        }
      }

      return {
        domainProfiles: domainProfiles.map(
          ({ name: label, key, features }) => ({
            label,
            value: key,
            features
          })
        ),
        profiles
      };
    } catch (err) {
      console.error(err);
      return {
        domainProfiles: [],
        profiles: []
      };
    }
  };

  const getViews = async () => {
    try {
      const result = await VIEWS.get();
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getToolTipText = txt => {
    return (
      <Row spacing={0}>
        <Item>
          <Text color={themeColors.dark}>{txt}</Text>
        </Item>
      </Row>
    );
  };

  const formatGroups = groups => {
    const nbGroups = groups.length;
    if (nbGroups === 0) {
      return <Text>Aucun</Text>;
    }
    if (nbGroups === 1) {
      return <Text color="global"> {groups[0] && groups[0].name} </Text>;
    }

    const others = nbGroups - 1;
    const str = ` ${others} ${pluralize('autre', others)} ${pluralize(
      'groupe',
      others
    )}`;

    const tooltipContent = () => {
      return (
        <Container>{groups.map(group => getToolTipText(group.name))}</Container>
      );
    };

    return (
      <Container>
        <Row spacing={2}>
          <Item flex>
            <Text color="global"> {groups[0].name}&nbsp;</Text>
          </Item>
          <Item flex>
            <Text> et&nbsp;</Text>
          </Item>
          <Item flex>
            <Tooltip title={tooltipContent()}>
              <Text color="global">{str}</Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  const formatDomains = (domains = []) => {
    const nbDomain = domains.length;
    if (nbDomain === 0) {
      return <Text>Aucun</Text>;
    }
    const mappedDomain = domains.map(
      el => domainConfigs.find(config => config.domain === el)?.name
    );

    if (nbDomain === 1) {
      return <Text color="global"> {mappedDomain[0]} </Text>;
    }

    const others = nbDomain - 1;
    const str = ` ${others} ${pluralize('autre', others)} ${pluralize(
      'domaine',
      others
    )}`;

    const tooltipContent = () => {
      return (
        <Container>{mappedDomain.map(name => getToolTipText(name))}</Container>
      );
    };
    return (
      <Container>
        <Row spacing={2}>
          <Item flex>
            <Text color="global">
              {' '}
              {mappedDomain[0]}
              &nbsp;
            </Text>
          </Item>
          <Item flex>
            <Text> et&nbsp;</Text>
          </Item>
          <Item flex>
            <Tooltip title={tooltipContent()}>
              <Text color="global">{str}</Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  const deleteUser = async userToDelete => {
    await USERS.deleteUser(userToDelete._id);
    await Promise.all(
      userToDelete.groups.map(async group => {
        GROUP.deleteUser({
          groupId: group._id,
          userId: userToDelete._id
        });
      })
    );
    if (userToDelete.adminRights?._id) {
      await USERS.deleteAdminRights(userToDelete.adminRights._id);
    }
  };

  const loadUsers = async () => {
    const users = await getUsers();

    const mapped = users.reduce((acc, item) => {
      const newItem = {};
      newItem._id = item._id;
      newItem.id = item._id;
      newItem.familyName = item.family_name;
      newItem.givenName = item.given_name;
      newItem.phoneNumber = item.phoneNumber;
      newItem.email = item.email;
      newItem.groups = formatGroups(item.groups || []);
      newItem.groupLength = item.groups?.length || 0;
      newItem.externalId = item.externalId;
      newItem.profileName =
        profileList.find(el => el.key === item.profile)?.name || 'Aucun';
      newItem.disableDelete = item._id === user._id;

      newItem.role =
        item.roles.filter(el => el.name !== 'SUPERADMIN')[0]?.name || 'AUCUN';
      newItem.domains = formatDomains(item.domains, domainConfigs);
      acc.push(newItem);
      return acc;
    }, []);
    setUserList(users);
    setRows(mapped);
  };

  const getDatas = async () => {
    const [
      users,
      adminRightsPresets,
      { domainProfiles, profiles },
      views,
      allDataProviders,
      rolesList
    ] = await Promise.all([
      getUsers(),
      getAdminRightsPresets(),
      getProfiles(),
      getViews(),
      getDataProviders(),
      getRoles().then(resRoles =>
        isSimplify
          ? resRoles.filter(({ name }) =>
              ['MAGASIN', 'SELLER', 'STANDARD'].includes(name)
            )
          : resRoles
      )
    ]);
    setProfileList(profiles);
    setViewList(views);
    setDataProviders(allDataProviders);

    if (isSimplify) {
      const [userShopGroups, userNetworkGroupsResponse] = await Promise.all([
        ACTIVATION.getUserActivationShopGoups(),
        GROUP.getGroupSearch({
          limit: 1000,
          withActivationShopGroupId: true
        })
      ]);

      setShopGroups(userShopGroups || []);
      setNetworkGroups(userNetworkGroupsResponse?.data || []);
    }

    const tmpRoles = !isSimplify
      ? rolesList
      : rolesList.filter(el => el.name !== 'SUPERADMIN');

    const profileKeysToShow = profiles.reduce((acc, curr) => {
      if (curr.showInSimplified) {
        acc.push(curr.key);
      }
      return acc;
    }, []);
    const mapped = users.reduce((acc, item) => {
      if (
        isSimplify &&
        !item.roles.some(el => el.name === 'MAGASIN') &&
        profileKeysToShow.indexOf(item.profile) === -1
      ) {
        return acc;
      }

      const newItem = {};
      newItem._id = item._id;
      newItem.id = item._id;
      newItem.familyName = item.family_name;
      newItem.givenName = item.given_name;
      newItem.phoneNumber = item.phoneNumber;
      newItem.email = item.email;
      newItem.groups = formatGroups(item.groups || []);
      newItem.groupLength = item.groups?.length || 0;
      newItem.externalId = item.externalId;
      newItem.profileName =
        profiles.find(el => el.key === item.profile)?.name || 'Aucun';
      newItem.role =
        item.roles.filter(el => el.name !== 'SUPERADMIN')[0]?.name || 'AUCUN';
      newItem.domains = formatDomains(item.domains, domainConfigs);
      acc.push(newItem);
      return acc;
    }, []);
    setAdminPresetList(adminRightsPresets);
    setRoles(tmpRoles);

    const newRows = filterAllRows(searchFieldValue, mapped);
    setRows(newRows);
    const sortedDomains = domainConfigs
      .map(({ domain, name }) => ({
        key: domain,
        label: `${name} (${domain})`
      }))
      .sort((a, b) => {
        const labelA = a.label?.toLowerCase();
        const labelB = b.label?.toLowerCase();
        if (labelA > labelB) {
          return 1;
        }
        if (labelA < labelB) {
          return -1;
        }
        return 0;
      });
    setDomainList(sortedDomains);
    setProfileOptions(domainProfiles);
    setRestrictedProfiles(domainProfiles);
    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  useEffect(() => {
    if (loaded) {
      clearTimeout(timer);
      // eslint-disable-next-line
      timer = setTimeout(loadUsers, 500);
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [
    loaded,
    pageNumber,
    limit,
    searchFieldValue,
    restrictedProfiles,
    restrictedGroups
  ]);

  const handleCreateUser = async (
    { groups, adminRights, ...datas },
    shopDatas,
    isAddUserToShop = false
  ) => {
    try {
      const profile = profileOptions.find(
        el => el.value === datas.additionalInformation.profile
      );
      const canAccessBo =
        profile?.features?.canAccessBO || profile?.features?.isSimplified;
      if (isSimplify) {
        const newUser = await USERS.createUser(datas);
        if (newUser) {
          setModal('stepper');
        }
        setCurrentStep(1);

        try {
          if (!isAddUserToShop) {
            const activationShops = [];
            const defaultPreset = await ACTIVATION.getActivationDefaultPreset();
            await Promise.all(
              groups.map(async group => {
                GROUP.addUser({
                  groupId: group._id,
                  userId: newUser.externalId
                });
              })
            );
            const newRole = datas.roles.map(el => {
              return roles.find(item => item.id === el)?.name || 'unknown';
            });
            if (newRole.indexOf('ADMIN') !== -1) {
              await USERS.createAdminRights({
                userId: newUser.externalId,
                rights: adminRights
              });
            }
            const isAdmin = newRole.indexOf('ADMIN') !== -1;
            if (!isAdmin && canAccessBo) {
              // Création de l'admin right pour les utilisateurs BO simplifié
              await USERS.createAdminRights({
                userId: newUser.externalId,
                rights: simplifiedRights
              });
            }

            for await (const shop of shopDatas) {
              const insightShopData = {
                name: shop?.name,
                address: shop?.address,
                origin: shop?.origin,
                audienceType: shop?.audienceType,
                parameters: shop?.parameters
              };

              const activationShopData = {
                key: shop?.key,
                name: shop?.name,
                owner: newUser?.externalId,
                users: [newUser?.externalId],
                acceptedMedias: domainConfig?.acceptedMedias || [],
                domain: shop?.domain,
                create: true,
                siteUri: shop?.siteUrl
              };

              const { activationShop } = await CREATE_SHOP.createShop({
                activationShopData,
                insightShopData,
                owner: newUser
              });

              await GROUP.create({
                activationShopId: activationShop.id,
                userIds: [newUser.externalId],
                name: activationShop.name,
                presetId: defaultPreset?._id
              });

              activationShops.push(activationShop);
            }
            setCurrentStep(2);
            for await (const shopData of shopDatas) {
              const shop = shopGroups.find(
                el => el._id === shopData?.shopGroup
              );
              if (shop) {
                const shoplist = shop?.shops.concat(
                  activationShops
                    .filter(el =>
                      shopDatas
                        .filter(element => element?.shopGroup === shop?._id)
                        .map(item => item?.key)
                        .includes(el?.key)
                    )
                    .map(el => el?._id)
                );

                await ACTIVATION.updateActivationShopGoup(shop?._id, {
                  shops: shoplist
                });
              }
            }
          } else {
            await Promise.all(
              shopDatas.map(async linkedShop => {
                const condition =
                  linkedShop?._id &&
                  linkedShop?.group &&
                  Array.isArray(linkedShop?.users);

                if (condition) {
                  await GROUP.addUser({
                    groupId: linkedShop.group,
                    userId: newUser.externalId
                  });
                }
              })
            );
          }
        } catch (error) {
          const toDeleteUser = await selectUser(newUser.externalId);
          await deleteUser(toDeleteUser);
          closeModal();
          throw error;
        }
        setModal(null);
        setLoaded(false);
      } else {
        const newUser = await USERS.createUser(datas);
        if (newUser) {
          await Promise.all(
            groups.map(async group => {
              GROUP.addUser({
                groupId: group._id,
                userId: newUser.externalId
              });
            })
          );
          const newRole = datas.roles.map(el => {
            return roles.find(item => item.id === el)?.name || 'unknow';
          });
          if (newRole.indexOf('ADMIN') !== -1) {
            await USERS.createAdminRights({
              userId: newUser.externalId,
              rights: adminRights
            });
          }
          const isAdmin = newRole.indexOf('ADMIN') !== -1;

          if (!isAdmin && canAccessBo) {
            await USERS.createAdminRights({
              userId: newUser.externalId,
              rights: simplifiedRights
            });
          }
          setModal(null);
          setLoaded(false);
        }
      }
    } catch (error) {
      notify({
        message:
          'La création de votre utilisateur a échoué. Veuillez réessayer.',
        variant: 'error',
        time: 8000
      });
      console.error(error);
      throw error;
    }
  };

  const handleUpdateUser = async ({ groups, adminRights, ...datas }) => {
    await USERS.updateUser(selectedUser.graviteeId, datas);
    for await (const group of selectedUser.groups) {
      const toRemove = !groups.find(el => el._id === group._id);
      if (toRemove) {
        await GROUP.deleteUser({
          groupId: group._id,
          userId: selectedUser._id
        });
      }
    }
    for await (const item of groups) {
      const userInGroup = selectedUser.groups.find(el => el._id === item._id);
      if (!userInGroup) {
        await GROUP.addUser({
          groupId: item._id,
          userId: selectedUser._id
        });
      }
    }

    const newRole = datas.roles.map(el => {
      return roles.find(item => item.id === el)?.name || 'unknow';
    });
    const isAdmin = newRole.indexOf('ADMIN') !== -1;
    if (isAdmin) {
      if (!selectedUser.adminRights) {
        await USERS.createAdminRights({
          userId: selectedUser._id,
          rights: adminRights
        });
      } else {
        await USERS.updateAdminRights(selectedUser.adminRights._id, {
          rights: adminRights
        });
      }
      if (selectedUser._id === user._id) {
        setAdminRights(adminRights);
      }
    }

    const profile = profileOptions.find(
      el => el.value === datas.additionalInformation.profile
    );
    const canAccessBo =
      profile?.features?.canAccessBO || profile?.features?.isSimplified;
    if (!isAdmin && selectedUser.adminRights?._id && !canAccessBo) {
      await USERS.deleteAdminRights(selectedUser.adminRights._id);
    }
    if (!isAdmin && !selectedUser.adminRights?._id && canAccessBo) {
      // Création de l'admin right pour les utilisateurs BO simplifié
      await USERS.createAdminRights({
        userId: selectedUser._id,
        rights: simplifiedRights
      });
    }

    if (user.graviteeId === selectedUser.graviteeId) {
      await fetchUser();
    }
    setModal(null);
    setLoaded(false);
  };

  const handleDelete = async () => {
    await deleteUser(selectedUser);
    setLoaded(false);
    closeModal();
  };

  const addUser = () => {
    if (
      adminRightsContext?.general?.users?.create ||
      adminRightsContext?.general?.admin?.create ||
      isSimplify
    ) {
      setModal('modalCreate');
    } else {
      setModal('unauthorized');
    }
  };

  const updateUser = async id => {
    const currentUser = await selectUser(id);
    if (adminRightsContext?.general?.admin?.update) {
      return setModal('modalUpdate');
    }

    if (
      (adminRightsContext?.general?.users?.update || isSimplify) &&
      !currentUser.roles.find(el => el.name === 'ADMIN')
    ) {
      return setModal('modalUpdate');
    }

    return setModal('unauthorized');
  };

  const openModalDelete = async id => {
    const currentUser = await selectUser(id);
    if (adminRightsContext?.general?.admin?.delete) {
      return setModal('modalDelete');
    }

    if (
      (adminRightsContext?.general?.users?.delete || isSimplify) &&
      !currentUser.roles.find(el => el.name === 'ADMIN')
    ) {
      return setModal('modalDelete');
    }

    return setModal('unauthorized');
  };

  const onListLogs = async item => {
    await selectUser(item.id);

    const sessionLogs = await USERS.getUserLogs({
      userEmails: item.email ? [item.email] : [],
      eventKeys: ['session_started']
    });

    setLogs({ sessionStart: sessionLogs?.data || [] });
    setModal('modalLogs');
  };

  const lastGroupTaxonomyName = [
    domainConfig?.groupTaxonomy?.at(-1)?.name,
    domainConfig?.groupTaxonomy?.[0]?.name?.trim() || 'Magasin'
  ]
    .filter(Boolean)
    .join('/');
  const GroupTitle = !isSimplify ? 'Organisation' : lastGroupTaxonomyName;

  return (
    <Container>
      <Row spacing={4}>
        <Item xs={3}>
          <TextField
            onChange={e => {
              setPageNumber(0);
              setSearchFieldValue(e);
            }}
            value={searchFieldValue}
            title="Rechercher un utilisateur"
            placeholder={isSimplify ? 'Id, Email, Nom, ...' : 'Email, Nom, ...'}
            type="search"
            unit={
              <SearchIcon
                style={{
                  color: themeColors.light
                }}
              />
            }
          />
        </Item>
        <Item xs={3} justify="flex-start">
          <MultipleSelectSearch
            title={GroupTitle}
            fetchOptions={fetchGroups}
            values={restrictedGroups}
            onChange={options => {
              setRestrictedGroups(options);
            }}
            small
          />
        </Item>
        <Item xs={3} justify="flex-start">
          <MultipleSelectSearch
            title="Profils"
            options={profileOptions}
            values={restrictedProfiles}
            onChange={options => {
              setRestrictedProfiles(options);
            }}
            small
            selectAll
          />
        </Item>
        <Item justify="flex-end" xs>
          <ButtonCreate onClick={addUser} disabled={!loaded}>
            Ajouter un utilisateur
          </ButtonCreate>
          {openModal === 'modalCreate' && (
            <ModalForm
              open={openModal === 'modalCreate'}
              onClose={closeModal}
              onValidate={handleCreateUser}
              config={{
                rolesList: roles,
                adminPresetList,
                domainList,
                shopGroups,
                networkGroups,
                profileList,
                viewList,
                allDataProviders: dataProviders
              }}
            />
          )}
        </Item>
        <Item>
          <Loading loading={!loaded} />
          {loaded && (
            <TableData
              rows={rows}
              headers={[
                ...(isSimplify
                  ? [
                      {
                        label: 'ID Compte Utilisateur',
                        id: 'externalId',
                        sortKey: 'externalId'
                      }
                    ]
                  : []),
                {
                  label: 'Adresse email',
                  id: 'email',
                  sortKey: 'email'
                },
                {
                  label: 'Prénom',
                  id: 'givenName',
                  sortKey: 'givenName'
                },
                {
                  label: 'Nom',
                  id: 'familyName',
                  sortKey: 'familyName'
                },
                ...(!isSimplify
                  ? [
                      {
                        label: 'Organisation',
                        id: 'groups',
                        sortKey: 'groupLength'
                      }
                    ]
                  : []),
                {
                  label: 'Rôle',
                  id: 'role',
                  sortKey: 'role'
                },
                {
                  label: 'Profil',
                  id: 'profileName',
                  sortKey: 'profileName'
                },
                ...(!isSimplify ? [{ label: 'Domaines', id: 'domains' }] : [])
              ]}
              onUpdate={updateUser}
              onListLogs={onListLogs}
              onDelete={openModalDelete}
              pageNumber={pageNumber}
              numberPerRow={limit}
              overrideCount={totalUser}
              skipSlice
              getPageNumber={page => {
                setPageNumber(page);
              }}
              setNumberPerRow={newLimit => {
                setLimit(newLimit);
                setPageNumber(0);
              }}
            />
          )}
          {openModal === 'modalUpdate' && (
            <ModalForm
              open={openModal === 'modalUpdate'}
              onClose={closeModal}
              onValidate={handleUpdateUser}
              rolesList={roles}
              config={{
                rolesList: roles,
                selectedUser,
                adminPresetList,
                domainList,
                shopGroups,
                networkGroups,
                profileList,
                viewList,
                allDataProviders: dataProviders
              }}
            />
          )}
          {openModal === 'modalDelete' && (
            <ModalDelete
              open={openModal === 'modalDelete'}
              onClose={closeModal}
              onDelete={handleDelete}
            />
          )}
          {openModal === 'unauthorized' && (
            <UnauthorizedModal
              open={openModal === 'unauthorized'}
              onClose={closeModal}
            />
          )}
          {openModal === 'modalLogs' && (
            <ModalLogs
              name={selectedUser.email}
              title="Session utilisateur"
              datas={[
                {
                  headers: [
                    {
                      label: 'Début de la session',
                      key: 'createdAt',
                      format: ({ data }) => {
                        if (!data?.startTime) {
                          return 'N/A';
                        }
                        return (
                          <Text bold>
                            {moment(data.startTime).format(
                              'DD/MM/YYYY hh:mm:ss'
                            )}
                          </Text>
                        );
                      }
                    },
                    {
                      label: 'Durée',
                      key: 'createdAt',
                      format: ({ data }) => {
                        if (!data?.duration) {
                          return 'N/A';
                        }
                        return (
                          <Text color={themeColors.activation}>
                            {parseInt(data.duration / 60, 10)}min
                          </Text>
                        );
                      }
                    }
                  ],
                  label: 'Session start',
                  values: logs.sessionStart
                }
              ]}
              onClose={() => {
                closeModal();
              }}
            />
          )}
          {openModal === 'stepper' && <ModalStepper activeStep={currentStep} />}
        </Item>
      </Row>
    </Container>
  );
};

export default UserContainer;
